import { createRouter, createWebHistory } from 'vue-router';
import HomeView from '@/views/HomeView.vue';
import { useAuthStore } from '@/stores/authStore';
import { ROUTES } from './routeConstants';

const filterPath = (to, from, next) => {
  if (to.fullPath !== to.path) {
    next({ path: to.path });
  } else {
    next();
  }
};


const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    } else {
      return { top: 0, behavior: 'smooth' };
    } 
  },
  routes: [
    {
      path: ROUTES.HOME.path,
      name: ROUTES.HOME.name,
      meta: {
        requiresAuth: true,
        title: 'Home'
      },
      component: HomeView,
      beforeEnter: filterPath
    },
    {
      path: ROUTES.SIGN_IN_REGISTER.path,
      name: ROUTES.SIGN_IN_REGISTER.name,
      meta: {
        title: 'Sign In or Register',
      },
      component: () => import('@/components/registration/SignInRegister.vue'),
      beforeEnter: (to, from , next) => {
        const store = useAuthStore();
        if (store.authenticated) {
          next({ path: '/' });
        } else {
          filterPath(to, from ,next);
        }
      }
    }, 
    {
      path: ROUTES.MY_ORGANIZATION.path,
      name: ROUTES.MY_ORGANIZATION.name,
      meta: {
        requiresAuth: true,
        title: 'My Organization',
        breadcrumb: [
          { label: 'My Organization' }
        ]
      },
      component: () => import('../views/organization/MyOrganizationView.vue'),
      beforeEnter: filterPath
    },
    {
      path: ROUTES.REGISTRATION.path,
      name: ROUTES.REGISTRATION.name,
      meta: {
        title: 'HEP Registration',
      },
      component: () => import('../views/registration/RegistrationView.vue')
    },
    {
      path: ROUTES.ORGANIZATION_REGISTRATION.path,
      name: ROUTES.ORGANIZATION_REGISTRATION.name,
      meta: {
        title: 'Registration New Organization',
        instructions: 'Complete all required fields below to submit the registration.'
      },
      component: () => import('../views/registration/RegisterOrganizationView.vue')
    },
    {
      path: ROUTES.HCE_REGISTRATION.path,
      name: ROUTES.HCE_REGISTRATION.name,
      props: { orgType: '' },
      meta: {
        title: '',
        instructions: 'Complete all required fields below to submit the registration.'
      },
      component: () => import('../views/registration/RegisterOrganizationView.vue')
    },
    {
      path: ROUTES.CONTACT_REGISTRATION.path,
      name: ROUTES.CONTACT_REGISTRATION.name,
      props: { organizationId: null },
      meta: {
        title: 'Register Contact with Organzation',
        instructions: 'Complete all required fields below to submit the registration.'
      },
      component: () => import('../views/registration/RegisterContactView.vue')
    },
    {
      path: ROUTES.ANNUAL_REPORTS.path,
      name: ROUTES.ANNUAL_REPORTS.name,
      meta: {
        requiresAuth: true,
        title: 'Annual Reports',
        instructions: 'Below is a list of annual reports that need to be completed.',
        breadcrumb: [
          { label: 'Annual Reports' }
        ]
      },
      component: () => import('../views/reporting/AnnualReportsView.vue')
    },
    {
      path: ROUTES.CONVERSATION.path,
      name: ROUTES.CONVERSATION.name,
      meta: {
        requiresAuth: true,
        title: 'Conversation',
        instructions: 'Below are the details for the selected conversation.',
        breadcrumb: [
          { label: 'Conversations', route: '/conversations' },
          { label: 'Conversation' }
        ]
      },
      component: () => import('../views/conversation/ConversationView.vue')
    },
    {
      path: ROUTES.CONVERSATIONS.path,
      name: ROUTES.CONVERSATIONS.name,
      meta: {
        requiresAuth: true,
        title: 'Conversations',
        instructions: 'Below is a list of your conversations.',
        breadcrumb: [
          { label: 'Conversations' }
        ]
      },
      component: () => import('../views/conversation/ConversationsView.vue'),
      beforeEnter: filterPath
    },
    {
      path: ROUTES.NEW_CONVERSATION.path,
      name: ROUTES.NEW_CONVERSATION.name,
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        title: 'New Conversation',
        instructions: 'To start a conversation with a reporting entity, select the organization, one or more participants, and enter a subject. Next, click the "Select Message Notes" button to auto-populate any item specific notes that have been entered during the report review process. Once message notes are selected, they will be added to the message window and additional information and/or formatting can be applied. By default, when selecting an organization only organizations that have message notes will auto-populate in the drop-down input. To access all organizations, click the Messages Only button to toggle to "All Organizations".',
        breadcrumb: [
          { label: 'Conversations', route: '/conversations' },
          { label: 'New Conversation' }
        ]
      },
      component: () => import('../modules/program/views/NewConversationView.vue'),
      beforeEnter: filterPath
    },
    {
      path: ROUTES.NEW_GENERAL_NOTICE.path,
      name: ROUTES.NEW_GENERAL_NOTICE.name,
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        title: 'New General Notice',
        instructions: 'To start a conversation with a reporting entity, select the organization, one or more participants, and enter a subject. Next, click the "Select Message Notes" button to auto-populate any item specific notes that have been entered during the item review process.  Once message notes are selected, they will be added to the message window and additional information and/or formatting can be applied.  When selecting an organization, only organizations that have active item batches will auto-populate in the drop-down input. To access all organizations. click the "include all organizations" checkbox.',
        breadcrumb: [
          { label: 'Conversations', route: '/conversations' },
          { label: 'New General Notice' }
        ]
      },
      component: () => import('../modules/program/views/NewGeneralNoticeView.vue'),
      beforeEnter: filterPath
    },
    {
      path: ROUTES.VIEW_REPORTING_EXCEPTION.path,
      name: ROUTES.VIEW_REPORTING_EXCEPTION.name,
      meta: {
        requiresAuth: true,
        title: 'Reporting Exception',
        instructions: 'Below are the details for the selected reporting exception.',
        breadcrumb: [
          { label: 'Reporting Exceptions', route: '/program/reportingexceptions' },
          { label: 'Reporting Exception' }
        ]
      },
      component: () => import('../views/reporting/ViewReportingExceptionView.vue')
    },
    {
      path: ROUTES.UNAUTHORIZED.path,
      name: ROUTES.UNAUTHORIZED.name,
      meta: {
        title: 'Unauthorized'
      },
      beforeEnter: filterPath,
      component: () => import('../views/UnauthorizedView.vue')
    },    
    {
      path: ROUTES.NOT_FOUND.path,
      name: ROUTES.NOT_FOUND.name,
      component: () => import('../components/NotFound.vue'),
      meta: {
        title: 'Not Found',
      },
      beforeEnter: filterPath
    },
 ]
});

router.beforeEach(async (to, from, next) => {
 handleNavigation();

  function handleNavigation() {
    if (to.meta.requiresAuth) {
      const store = useAuthStore();
      if (!store.authenticated) {
        next({ path: ROUTES.SIGN_IN_REGISTER.path });
      } else if (to.meta.requiresAdmin && !store.user.isAdminUser) {
          next({ path: ROUTES.UNAUTHORIZED.path });
      } else {
        next();
      }
    } else {
      next();
    }
  }
});

export default router;
